import React, { useState, useEffect, useRef, memo } from "react";
import { NavLink } from "react-router-dom";
import { Menu, X, ChevronDown, Globe, LogIn, LogOut, Mail, Lock, XCircle } from "lucide-react";
import logo from "../assets/Img/airaa-logo-white.webp";
import { useTranslation } from "react-i18next";

// ==================== AUTH MODAL ====================
const AuthModal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      style={{ zIndex: 9999 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6 relative">
        <button onClick={onClose} aria-label="Close modal" className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition">
          <XCircle size={20} />
        </button>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{title}</h2>
        {children}
      </div>
    </div>
  );
};

// ==================== CLOCK COMPONENT ====================
const LiveClock = memo(() => {
  const [currentTime, setCurrentTime] = useState(new Date().toLocaleTimeString());
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date().toLocaleTimeString()), 1000);
    return () => clearInterval(interval);
  }, []);
  return <p className="text-xs text-gray-600">{currentTime}</p>;
});

// ==================== MAIN NAVBAR ====================
const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

  // Track viewport width to decide desktop vs mobile
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const checkWidth = () => {
      // Use 1024px as the breakpoint for desktop menu
      setIsDesktop(window.innerWidth >= 1024);
      // Close mobile menu if resized to desktop
      if (window.innerWidth >= 1024) setIsOpen(false);
    };
    checkWidth();
    window.addEventListener("resize", checkWidth);
    return () => window.removeEventListener("resize", checkWidth);
  }, []);

  const aboutTimer = useRef(null);
  const servicesTimer = useRef(null);

  // Auth state
  const [auth, setAuth] = useState({ isAuthenticated: false, user: null, token: null });
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [authDropdownOpen, setAuthDropdownOpen] = useState(false);
  const authTimer = useRef(null);

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === "en" ? "hi" : "en");
  };

  const handleAuthEnter = () => { clearTimeout(authTimer.current); setAuthDropdownOpen(true); };
  const handleAuthLeave = () => { authTimer.current = setTimeout(() => setAuthDropdownOpen(false), 120); };

  // Load auth from localStorage
  useEffect(() => {
    const storedAuth = localStorage.getItem("airaa_auth");
    if (storedAuth) {
      try { setAuth(JSON.parse(storedAuth)); } catch (e) { console.error("Failed to parse auth"); }
    }
  }, []);

  useEffect(() => {
    if (auth.isAuthenticated) localStorage.setItem("airaa_auth", JSON.stringify(auth));
    else localStorage.removeItem("airaa_auth");
  }, [auth]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) { setAuthError("Please enter both email and password"); return; }
    if (loginEmail !== "admin@airaa.co.in" || loginPassword !== "admin@airaa2026") { setAuthError("Invalid credentials"); return; }
    setAuth({
      isAuthenticated: true,
      user: { email: loginEmail, name: "admin", role: "admin" },
      token: "mock-jwt-token-" + Date.now(),
    });
    setShowLoginModal(false);
    setLoginEmail("");
    setLoginPassword("");
    setAuthError("");
  };

  const handleLogout = () => {
    setAuth({ isAuthenticated: false, user: null, token: null });
    setIsOpen(false);
  };

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === "Escape") { setShowLoginModal(false); setIsOpen(false); } };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Hover handlers
  const handleAboutEnter = () => { clearTimeout(aboutTimer.current); setAboutOpen(true); };
  const handleAboutLeave = () => { aboutTimer.current = setTimeout(() => setAboutOpen(false), 120); };
  const handleServicesEnter = () => { clearTimeout(servicesTimer.current); setServicesOpen(true); };
  const handleServicesLeave = () => { servicesTimer.current = setTimeout(() => setServicesOpen(false), 120); };

  const dropdownMenu = "absolute left-0 top-full mt-1 w-56 bg-white text-gray-800 shadow-xl rounded-lg overflow-hidden border border-gray-100";
  const dropdownItem = "block px-4 py-2.5 text-sm hover:bg-green-50 hover:text-green-700 transition-colors duration-150";

  const navLinkClass = ({ isActive }) =>
    isActive ? "text-orange-300 font-bold" : "text-white hover:text-yellow-200 transition";

  return (
    <>
      {/* ===== FIXED Z-INDEX WRAPPER ===== */}
      <nav className="fixed top-0 left-0 w-full" style={{ zIndex: 1000 }}>

        {/* ===== TOP BAR ===== */}
        <div
          className="w-full bg-white text-green-600 transition-all duration-300 overflow-hidden"
          style={{
            maxHeight: isScrolled ? "0px" : "80px",
            opacity: isScrolled ? 0 : 1,
            paddingTop: isScrolled ? 0 : undefined,
            paddingBottom: isScrolled ? 0 : undefined,
          }}
        >
          <div className="container mx-auto px-4 py-2 flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <img src={logo} alt="AIRAA Logo" className="w-12 h-12 object-contain flex-shrink-0" />
              <div>
                <span className="block text-xs sm:text-sm md:text-base lg:text-lg font-semibold text-blue-700 uppercase leading-tight">
                  ALL INDIA RECRUITING AGENCY ASSOCIATION
                </span>
                <span className="block text-xs sm:text-xs md:text-sm text-blue-700 leading-tight">
                  अखिल भारतीय भर्ती एजेंसी एसोसिएशन
                </span>
              </div>
            </div>
            {/* Show phone/clock only on desktop */}
            {isDesktop && (
              <div className="text-right flex flex-col items-end gap-1 flex-shrink-0">
                <p className="text-sm font-medium text-green-600">
                  📞 <a href="tel:+917738355865">+91 7738355865</a>
                </p>
                <LiveClock />
                <button
                  onClick={toggleLanguage}
                  aria-label={i18n.language === "en" ? "Switch to Hindi" : "Switch to English"}
                  className="flex items-center gap-1 text-xs text-gray-600 hover:text-blue-700 transition font-medium"
                >
                  <Globe size={13} className="text-blue-600" />
                  {i18n.language === "en" ? "हिंदी" : "English"}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ===== MAIN NAVBAR BAR ===== */}
        <div className="p-1 w-full bg-gradient-to-r from-green-700 to-blue-700 shadow-lg">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">

            {/* Mobile: show logo/brand name */}
            {!isDesktop && (
              <NavLink to="/" className="text-white text-sm font-semibold flex items-center gap-2" onClick={() => setIsOpen(false)}>
                <span className="leading-tight text-xs">Home</span>
              </NavLink>
            )}

            {/* ===== DESKTOP LINKS ===== */}
            {isDesktop && (
              <div className="flex items-center gap-4 xl:gap-6 text-sm xl:text-base font-medium tracking-wide flex-wrap">
                <NavLink className={navLinkClass} to="/">{t("home")}</NavLink>

                {/* About Dropdown */}
                <div className="relative" style={{ zIndex: 1001 }} onMouseEnter={handleAboutEnter} onMouseLeave={handleAboutLeave}>
                  <button aria-label="About menu" aria-expanded={aboutOpen} className="flex items-center gap-1 text-white hover:text-yellow-200 transition whitespace-nowrap">
                    <span>{t("about")}</span>
                    <ChevronDown size={14} className={`transition-transform duration-200 ${aboutOpen ? "rotate-180" : ""}`} />
                  </button>
                  {aboutOpen && (
                    <div className={dropdownMenu} style={{ zIndex: 1002 }}>
                      <NavLink to="/about-airaa" className={dropdownItem} onClick={() => setAboutOpen(false)}>{t("about")}</NavLink>
                      <NavLink to="/gallery" className={dropdownItem} onClick={() => setAboutOpen(false)}>{t("gallerytitle")}</NavLink>
                      <NavLink to="/presidentmessage" className={dropdownItem} onClick={() => setAboutOpen(false)}>{t("message")}</NavLink>
                      <NavLink to="/news" className={dropdownItem} onClick={() => setAboutOpen(false)}>{t("news")}</NavLink>
                      <NavLink to="/certificate" className={dropdownItem} onClick={() => setAboutOpen(false)}>Certificate</NavLink>
                    </div>
                  )}
                </div>

                {/* Services Dropdown */}
                <div className="relative" style={{ zIndex: 1001 }} onMouseEnter={handleServicesEnter} onMouseLeave={handleServicesLeave}>
                  <button aria-label="Member Services menu" aria-expanded={servicesOpen} className="flex items-center gap-1 text-white hover:text-yellow-200 transition whitespace-nowrap">
                    <span>{t("Services")}</span>
                    <ChevronDown size={14} className={`transition-transform duration-200 ${servicesOpen ? "rotate-180" : ""}`} />
                  </button>
                  {servicesOpen && (
                    <div className={dropdownMenu} style={{ zIndex: 1002 }}>
                      <NavLink to="/why-join-airaa" className={dropdownItem} onClick={() => setServicesOpen(false)}>{t("Why Join AIRAA")}</NavLink>
                      <NavLink to="/member-services" className={dropdownItem} onClick={() => setServicesOpen(false)}>Membership & Services</NavLink>
                      <NavLink to="/registration" className={dropdownItem} onClick={() => setServicesOpen(false)}>{t("Registration")}</NavLink>
                      <NavLink to="/membership" className={dropdownItem} onClick={() => setServicesOpen(false)}>Membership & Certificate</NavLink>
                      <NavLink to="/helpline" className={dropdownItem} onClick={() => setServicesOpen(false)}>{t("Helpline")}</NavLink>
                    </div>
                  )}
                </div>

                <NavLink className={navLinkClass} to="/socialresponsibility">Initiatives</NavLink>
                <NavLink className={navLinkClass} to="/importantlinks">{t("Important Links")}</NavLink>
                <NavLink className={navLinkClass} to="/contact-us">{t("contact")}</NavLink>
              </div>
            )}

            {/* ===== MOBILE HAMBURGER ===== */}
            {!isDesktop && (
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="text-white p-1 ml-auto"
                aria-label="Toggle menu"
              >
                {isOpen ? <X size={26} /> : <Menu size={26} />}
              </button>
            )}
          </div>

          {/* ===== MOBILE DROPDOWN MENU ===== */}
          {!isDesktop && isOpen && (
            <div className="bg-gradient-to-b from-green-800 to-blue-900 text-white border-t border-white/10 max-h-screen overflow-y-auto">

              {/* Home */}
              <NavLink
                to="/"
                className={({ isActive }) =>
                  `block px-5 py-3 text-sm font-medium border-b border-white/10 ${isActive ? "text-yellow-300 bg-black/10" : "hover:bg-white/10"}`
                }
                onClick={() => setIsOpen(false)}
              >
                {t("home")}
              </NavLink>

              {/* Mobile About Accordion */}
              <div className="border-b border-white/10">
                <button
                  className="w-full flex items-center justify-between px-5 py-3 text-sm font-medium hover:bg-white/10 transition"
                  onClick={() => setMobileAboutOpen(!mobileAboutOpen)}
                >
                  <span>{t("about")}</span>
                  <ChevronDown size={16} className={`transition-transform duration-200 ${mobileAboutOpen ? "rotate-180" : ""}`} />
                </button>
                {mobileAboutOpen && (
                  <div className="bg-black/20">
                    {[
                      { to: "/about-airaa", label: t("about") },
                      { to: "/gallery", label: t("gallerytitle") },
                      { to: "/presidentmessage", label: t("message") },
                      { to: "/news", label: t("news") },
                      { to: "/certificate", label: "Certificate" },
                    ].map(({ to, label }) => (
                      <NavLink
                        key={to}
                        to={to}
                        className={({ isActive }) =>
                          `block pl-8 pr-5 py-2.5 text-sm ${isActive ? "text-yellow-300" : "text-white/85 hover:text-white hover:bg-white/10"} transition`
                        }
                        onClick={() => { setMobileAboutOpen(false); setIsOpen(false); }}
                      >
                        {label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile Services Accordion */}
              <div className="border-b border-white/10">
                <button
                  className="w-full flex items-center justify-between px-5 py-3 text-sm font-medium hover:bg-white/10 transition"
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                >
                  <span>{t("Services")}</span>
                  <ChevronDown size={16} className={`transition-transform duration-200 ${mobileServicesOpen ? "rotate-180" : ""}`} />
                </button>
                {mobileServicesOpen && (
                  <div className="bg-black/20">
                    {[
                      { to: "/why-join-airaa", label: t("Why Join AIRAA") },
                      { to: "/member-services", label: "Membership & Services" },
                      { to: "/registration", label: t("Registration") },
                      { to: "/membership", label: "Membership & Certificate" },
                      { to: "/helpline", label: t("Helpline") },
                    ].map(({ to, label }) => (
                      <NavLink
                        key={to}
                        to={to}
                        className={({ isActive }) =>
                          `block pl-8 pr-5 py-2.5 text-sm ${isActive ? "text-yellow-300" : "text-white/85 hover:text-white hover:bg-white/10"} transition`
                        }
                        onClick={() => { setMobileServicesOpen(false); setIsOpen(false); }}
                      >
                        {label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>

              {/* Other mobile links */}
              {[
                { to: "/socialresponsibility", label: "Initiatives" },
                { to: "/importantlinks", label: t("Important Links") },
                { to: "/contact-us", label: t("contact") },
              ].map(({ to, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    `block px-5 py-3 text-sm font-medium border-b border-white/10 ${isActive ? "text-yellow-300 bg-black/10" : "hover:bg-white/10"}`
                  }
                  onClick={() => setIsOpen(false)}
                >
                  {label}
                </NavLink>
              ))}

              {/* Mobile phone + language toggle */}
              <div className="px-5 py-3 border-t border-white/10 flex flex-col gap-2">
                <p className="text-sm text-green-300">📞 <a href="tel:+917738355865">+91 7738355865</a></p>
                <button
                  onClick={toggleLanguage}
                  aria-label={i18n.language === "en" ? "Switch to Hindi" : "Switch to English"}
                  className="flex items-center gap-2 text-white/80 hover:text-white transition text-sm font-medium"
                >
                  <Globe size={14} className="opacity-70" />
                  {i18n.language === "en" ? "हिंदी में देखें" : "View in English"}
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* ===== LOGIN MODAL ===== */}
      <AuthModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} title="Login to AIRAA">
        <form onSubmit={handleLogin} className="space-y-4">
          {authError && <p className="text-red-500 text-sm bg-red-50 px-3 py-2 rounded">{authError}</p>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="you@example.com"
                autoFocus
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="••••••••"
              />
            </div>
          </div>
          <button type="submit" className="w-full bg-green-700 hover:bg-green-800 text-white py-2.5 rounded-md transition font-medium">
            Sign In
          </button>
          <p className="text-center text-sm text-gray-500">
            New member?{" "}
            <NavLink to="/registration" onClick={() => setShowLoginModal(false)} className="text-green-700 hover:underline font-medium">
              Register here
            </NavLink>
          </p>
        </form>
      </AuthModal>

    </>
  );
};

export default Navbar;